import React, { Fragment, useEffect, useState } from 'react';
import { Grid } from 'semantic-ui-react';

import { array, shape, bool, any } from 'prop-types';
import uuidv4 from 'uuid';
import { breakpointsNumber } from '../../styles';
import { GridNumbers, ToolPermissions, BrandHelper } from '../../utils';
import { InfoTile } from '../../components';
import {
  newPartsTileBG,
  newRegisterTileBG,
  newMarketingTileBG,
  newSellingTileBG,
  newLearningTileBG,
  newManualsTileBG,
  newWarrantyClaimBG
} from '../../images';

const marketingItems = [
  {
    name: 'Brochures',
    url: '/brochures',
    id: uuidv4(),
    toolPermissionKey: ToolPermissions.BROCHURES
  },
  {
    name: 'Marketing Assets',
    url:
      'https://agidealersupport.zendesk.com/hc/en-us/categories/360005747753-Marketing-Assets-New-content-added-April-2026',
    id: uuidv4(),
    external: true,
    toolPermissionKey: ToolPermissions.LOGOS
  },
  {
    name: 'Price Lists',
    url: '/priceLists',
    id: uuidv4(),
    toolPermissionKey: ToolPermissions.PRICE_LISTS
  }
];

const Tools = ({ partsBrands, manualBrands, allBrands, authUser }) => {
  const isSmallScreenInitial =
    window.innerWidth <= breakpointsNumber.largeTablet;
  const [isSmallScreen, setIsSmallScreen] = useState(isSmallScreenInitial);

  const handleResize = () => {
    setIsSmallScreen(window.innerWidth <= breakpointsNumber.largeTablet);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getGridLayoutRows = tiles => {
    let poppedTiles;
    const gridRows = [];
    const { length } = tiles;
    switch (length % 3) {
      case 1:
        // means we have 4, 7 or 10 tiles -- which means we need:
        // two rows of two before the rows of three
        poppedTiles = tiles.splice(0, 4);
        break;
      case 2:
        // means we have 5, or 8 tiles -- which means we need:
        // two rows of two before the rows of three
        poppedTiles = tiles.splice(0, 2);
        break;
      default:
        poppedTiles = [];
        break;
    }

    const numOfTwoColRows = poppedTiles.length / 2;
    const numOfThreeColRows = tiles.length / 3;

    for (let i = 0; i < numOfTwoColRows; i++) {
      const colsPerRow = 2;
      const rowId = i + numOfTwoColRows;
      const rowStartIdx = colsPerRow * i;
      const rowEndIdx = colsPerRow * (i + 1);
      const rowTiles = poppedTiles.slice(rowStartIdx, rowEndIdx);
      gridRows.push({ rowTiles, rowId });
    }

    for (let i = 0; i < numOfThreeColRows; i++) {
      const colsPerRow = 3;
      const rowId = i + numOfThreeColRows;
      const rowStartIdx = colsPerRow * i;
      const rowEndIdx = colsPerRow * (i + 1);
      const rowTiles = tiles.slice(rowStartIdx, rowEndIdx);
      gridRows.push({ rowTiles, rowId });
    }

    return gridRows;
  };

  const renderTileColumn = (tile, rowId) => (
    <Grid.Column
      tablet={isSmallScreen ? GridNumbers.base : undefined}
      key={tile.id}
      data-testid={`tools-grid-column-${rowId}`}
    >
      <InfoTile
        title={tile.title}
        description={tile.description}
        ctaText={tile.ctaText}
        issmallbutton={tile.issmallbutton}
        backgroundImage={tile.backgroundImage}
        dropDownItems={tile.dropDownItems}
        external={tile.external}
        defaultDropDownText={tile.defaultDropDownText}
        ctaLink={tile.ctaLink}
        id={tile.id}
      />
    </Grid.Column>
  );

  const renderGridRow = (rowTiles, rowId) => {
    return (
      <Grid columns={rowTiles.length} stretched key={rowId}>
        {rowTiles.map((tile, i) => renderTileColumn(tile, `${rowId}-${i}`))}
      </Grid>
    );
  };

  const filterEmptyDropDownTiles = tile => {
    if (tile.dropDownItems && tile.dropDownItems.length === 0) return false;
    return true;
  };

  const tiles = [
    {
      title: 'Marketing',
      description:
        'Get a quick access to all logos, brochures and marketing help you need.',
      dropDownItems: ToolPermissions.filterAuthorizedTools(
        marketingItems,
        authUser
      ),
      ctaText: 'Go',
      issmallbutton: true,
      backgroundImage: newMarketingTileBG,
      defaultDropDownText: 'Select a page',
      id: uuidv4()
    },
    {
      title: 'Manuals',
      description:
        'Easily find and download product manuals from the brands you carry.',
      ctaText: 'Go',
      issmallbutton: true,
      backgroundImage: newManualsTileBG,
      dropDownItems: manualBrands,
      defaultDropDownText: 'Select a brand',
      id: uuidv4(),
      toolPermissionKey: ToolPermissions.MANUALS
    },
    {
      title: 'Warranty',
      description:
        'Submit a claim request for AGI farm equipment products still under warranty.',
      ctaText: 'Start A New Claim',
      backgroundImage: newWarrantyClaimBG,
      id: uuidv4(),
      ctaLink: '/warranty-claims'
    },
    {
      title: 'Parts',
      description:
        'You can easily order online the parts you need for repair from the brands you carry.',
      ctaText: 'Go',
      issmallbutton: true,
      backgroundImage: newPartsTileBG,
      dropDownItems: partsBrands,
      external: true,
      defaultDropDownText: 'Select a brand',
      id: uuidv4(),
      toolPermissionKey: ToolPermissions.PARTS,
      hasToolOverride: BrandHelper.hasAuthorizedPartBrands(partsBrands)
    },
    {
      title: 'Register A Product',
      description:
        'Simply scan a product QR code from your phone or enter the serial number.',
      ctaText: 'Register now',
      backgroundImage: newRegisterTileBG,
      external: true,
      id: uuidv4(),
      ctaLink: process.env.REACT_APP_WARRANTY
    }
  ];

  const authorizedTiles = ToolPermissions.filterAuthorizedTools(
    tiles,
    authUser,
    'dropDownItems'
  ).filter(filterEmptyDropDownTiles);

  const rows = getGridLayoutRows(authorizedTiles);

  return (
    <Fragment>
      {rows.map(row => renderGridRow(row.rowTiles, row.rowId))}
    </Fragment>
  );
};

Tools.propTypes = {
  partsBrands: array,
  manualBrands: array,
  allBrands: array,
  authUser: shape({
    roles: any.isRequired,
    tools: shape({
      brochures: bool.isRequired,
      dealerSuccess: bool.isRequired,
      learning: bool.isRequired,
      logos: bool.isRequired,
      manuals: bool.isRequired,
      parts: bool.isRequired,
      productRegistration: bool.isRequired,
      selling: bool.isRequired,
      warrantyClaims: bool.isRequired,
      priceLists: bool.isRequired
    }).isRequired
  }).isRequired
};

Tools.defaultProps = {
  partsBrands: null,
  allBrands: null,
  authUser: {
    tools: {
      brochures: false,
      dealerSuccess: false,
      learning: false,
      logos: false,
      manuals: false,
      parts: false,
      productRegistration: false,
      selling: false,
      warrantyClaims: false,
      priceLists: false
    }
  }
};

export default Tools;
